import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InvitationRSVPForm from "./InvitationRSVPForm";
import { getInvitationBySlug, getUserInvitations } from "../../utils/invitationsApi";
import { useAuth } from "../../contexts/AuthContext";
import "./Invitations.css";
import {
  DEFAULT_FONT_SETTINGS,
  FONT_SETTING_KEYS,
  buildGoogleFontsUrl,
  isGoogleFontFamily,
  normalizeFontDescriptor,
  sanitizeFontSettings,
} from "./invitationFonts";
import {
  formatMkFullDate,
  formatMkPrimaryDate,
  formatMkTime,
} from "./invitationDateUtils";

const PUBLIC_GOOGLE_FONT_LINK_ID = "invitation-public-google-fonts";

const extractTemplateFontDefaults = (fontSettings) => {
  if (!fontSettings || typeof fontSettings !== "object") {
    return {};
  }
  return Object.entries(fontSettings).reduce((acc, [key, value]) => {
    const asArray = Array.isArray(value) ? value : [value];
    const normalizedOptions = asArray
      .map((entry) => normalizeFontDescriptor(entry))
      .filter(Boolean);
    if (normalizedOptions.length) {
      acc[key] = normalizedOptions[0];
    }
    return acc;
  }, {});
};

const InvitationPublicPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const guestId = searchParams.get("guest") || "";
  const guestName = searchParams.get("name") || "";
  const { authPayload } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [countdownParts, setCountdownParts] = useState(null);
  const [countdownFinished, setCountdownFinished] = useState(false);
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState(null);
  const [agendaProgress, setAgendaProgress] = useState(0);
  const [activeAgendaIndex, setActiveAgendaIndex] = useState(0);
  const agendaRef = useRef(null);

  useEffect(() => {
    const loadInvitation = async () => {
      try {
        setLoading(true);
        const data = await getInvitationBySlug(slug);
        setInvitation(data);
      } catch (err) {
        console.error(err);
        setError(err.message || "Поканата не беше пронајдена.");
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [slug]);

  useEffect(() => {
    const checkOwnership = async () => {
      if (!authPayload?.id_token || !invitation?.id) {
        setIsOwner(false);
        return;
      }
      try {
        const userInvitations = await getUserInvitations();
        const owns = userInvitations?.some(
          (inv) => inv.id === invitation.id || inv.slug === invitation.slug,
        );
        setIsOwner(Boolean(owns));
      } catch (err) {
        console.warn("Unable to verify invitation ownership", err);
        setIsOwner(false);
      }
    };

    checkOwnership();
  }, [authPayload?.id_token, invitation]);

  useEffect(() => {
    if (!fullscreenImageUrl) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setFullscreenImageUrl(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [fullscreenImageUrl]);

  const rawTemplateConfig = invitation?.template?.json_structure;

  const templateConfig = useMemo(() => {
    if (!rawTemplateConfig) return {};
    if (typeof rawTemplateConfig === "string") {
      try {
        return JSON.parse(rawTemplateConfig);
      } catch {
        return {};
      }
    }
    return rawTemplateConfig;
  }, [rawTemplateConfig]);

  const templateFontDefaults = useMemo(
    () => extractTemplateFontDefaults(templateConfig?.font_settings),
    [templateConfig]
  );

  const invitationFontSettings = useMemo(
    () => sanitizeFontSettings(invitation?.font_settings),
    [invitation?.font_settings]
  );

  const combinedFontSettings = useMemo(() => {
    const map = {};
    FONT_SETTING_KEYS.forEach((key) => {
      map[key] =
        invitationFontSettings[key] ||
        templateFontDefaults[key] ||
        DEFAULT_FONT_SETTINGS[key] ||
        null;
    });
    return map;
  }, [invitationFontSettings, templateFontDefaults]);

  const fontStyleMap = useMemo(() => {
    const styles = {};
    FONT_SETTING_KEYS.forEach((key) => {
      const descriptor = combinedFontSettings[key];
      if (descriptor?.family) {
        styles[key] = {
          fontFamily: descriptor.family,
          ...(descriptor.weight ? { fontWeight: descriptor.weight } : {}),
          ...(descriptor.style ? { fontStyle: descriptor.style } : {}),
        };
      } else {
        styles[key] = {};
      }
    });
    return styles;
  }, [combinedFontSettings]);

  const googleFontsSelection = useMemo(() => {
    return FONT_SETTING_KEYS.map((key) => combinedFontSettings[key]).filter(
      (descriptor) =>
        descriptor?.family && isGoogleFontFamily(descriptor.family)
    );
  }, [combinedFontSettings]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }
    const href = buildGoogleFontsUrl(googleFontsSelection);
    const existingLink = document.getElementById(PUBLIC_GOOGLE_FONT_LINK_ID);
    if (!href) {
      if (existingLink) {
        existingLink.remove();
      }
      return undefined;
    }
    if (existingLink && existingLink.getAttribute("href") === href) {
      return undefined;
    }
    const link = document.createElement("link");
    link.id = PUBLIC_GOOGLE_FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = href;
    if (existingLink) {
      existingLink.remove();
    }
    document.head.appendChild(link);
    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [googleFontsSelection]);

  const theme = useMemo(() => {
    const palette = templateConfig?.palette || {};
    const colors = invitation?.theme_colors;
    const templatePrimary = palette.primary || "#2d2a26";

    return {
      primary:
        (colors && colors.primary) ||
        templatePrimary,
      secondary:
        (colors && colors.secondary) ||
        templatePrimary ||
        "#b89b5e",
    };
  }, [invitation, templateConfig]);

  const backgroundStyles = useMemo(() => {
    const styles = {
      backgroundColor: theme.primary,
    };
    if (templateConfig?.backgroundImage) {
      styles.backgroundImage = `url(${templateConfig.backgroundImage})`;
      styles.backgroundSize = "cover";
      styles.backgroundPosition = "center";
      styles.backgroundRepeat = "no-repeat";
    }
    return styles;
  }, [templateConfig, theme.primary]);

  const titleParts = useMemo(() => {
    if (!invitation?.title) return [];
    return invitation.title
      .split("&")
      .map((part) => part.trim())
      .filter(Boolean);
  }, [invitation?.title]);

  const eventDate = useMemo(() => {
    return invitation?.event_date ? new Date(invitation.event_date) : null;
  }, [invitation?.event_date]);
  const primaryDateText = useMemo(() => {
    return formatMkPrimaryDate(eventDate);
  }, [eventDate]);

  const fullDateText = useMemo(() => {
    return formatMkFullDate(eventDate);
  }, [eventDate]);

  const timeText = useMemo(() => {
    return formatMkTime(eventDate);
  }, [eventDate]);

  const mapEmbedUrl = invitation?.venue_name
    ? `https://www.google.com/maps?q=${encodeURIComponent(
        invitation.venue_name,
      )}&output=embed`
    : null;

  const agendaItems = useMemo(() => {
    if (!invitation?.agenda_shown || !Array.isArray(invitation?.agenda_items)) {
      return [];
    }
    return invitation.agenda_items.filter(
      (item) =>
        item?.title || item?.description || item?.time || item?.address
    );
  }, [invitation?.agenda_shown, invitation?.agenda_items]);

  useEffect(() => {
    if (!agendaItems.length) {
      setAgendaProgress(0);
      setActiveAgendaIndex(0);
      return undefined;
    }

    const updateProgress = () => {
      if (!agendaRef.current) return;
      const rect = agendaRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const raw = (viewportHeight - rect.top) / (rect.height + viewportHeight * 0.2);
      const clamped = Math.max(0, Math.min(1, raw));
      const startThreshold = 0.18;
      const delayedProgress =
        clamped <= startThreshold
          ? 0
          : (clamped - startThreshold) / (1 - startThreshold);
      setAgendaProgress(Math.max(0, Math.min(1, delayedProgress)) * 100);
      const idx = Math.min(
        agendaItems.length - 1,
        Math.max(0, Math.floor(clamped * agendaItems.length))
      );
      setActiveAgendaIndex(idx);
    };

    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);

    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, [agendaItems.length]);

  const shareMetadata = useMemo(() => {
    const buildAbsoluteUrl = (value) => {
      if (!value) return null;
      if (value.startsWith("http://") || value.startsWith("https://")) {
        return value;
      }
      if (typeof window !== "undefined" && value.startsWith("/")) {
        return `${window.location.origin}${value}`;
      }
      return value;
    };

    if (!invitation) {
      return {
        title: "Wedding Invitation",
        description: "Ви благодариме за интересот за поканата.",
        image: null,
        url: typeof window !== "undefined" ? window.location.href : "",
      };
    }
    const baseTitle = invitation.title || "Wedding Invitation";
    const description =
      invitation.message ||
      (invitation.venue_name && fullDateText
        ? `${invitation.venue_name} · ${fullDateText}`
        : fullDateText || "Join us for a special celebration.");
    const image = buildAbsoluteUrl(
      invitation.cover_image_url ||
        invitation.template?.thumbnail_url ||
        templateConfig?.backgroundImage ||
        null
    );
    const url =
      typeof window !== "undefined"
        ? `${window.location.origin}/invite/${invitation.slug}`
        : "";
    return {
      title: baseTitle,
      description,
      image,
      url,
    };
  }, [invitation, fullDateText, templateConfig]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }
    const previousTitle = document.title;
    if (shareMetadata.title) {
      document.title = shareMetadata.title;
    }

    const metaEntries = [
      { type: "name", key: "description", content: shareMetadata.description },
      { type: "property", key: "og:title", content: shareMetadata.title },
      { type: "property", key: "og:description", content: shareMetadata.description },
      { type: "property", key: "og:type", content: "website" },
      { type: "property", key: "og:url", content: shareMetadata.url },
      { type: "property", key: "og:image", content: shareMetadata.image },
      { type: "name", key: "twitter:card", content: shareMetadata.image ? "summary_large_image" : "summary" },
      { type: "name", key: "twitter:title", content: shareMetadata.title },
      { type: "name", key: "twitter:description", content: shareMetadata.description },
      { type: "name", key: "twitter:image", content: shareMetadata.image },
    ];

    const updatedMeta = [];

    metaEntries.forEach(({ type, key, content }) => {
      if (!content) {
        return;
      }
      const selector = `meta[${type}="${key}"]`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(type, key);
        document.head.appendChild(tag);
      }
      updatedMeta.push({
        tag,
        previousContent: tag.getAttribute("content"),
      });
      tag.setAttribute("content", content);
    });

    return () => {
      document.title = previousTitle;
      updatedMeta.forEach(({ tag, previousContent }) => {
        if (previousContent) {
          tag.setAttribute("content", previousContent);
        } else {
          tag.remove();
        }
      });
    };
  }, [shareMetadata]);

  useEffect(() => {
    if (!invitation?.event_date) {
      setCountdownParts(null);
      setCountdownFinished(false);
      return;
    }

    const targetTime = new Date(invitation.event_date).getTime();
    let timerId;

    const updateCountdown = () => {
      const diff = targetTime - Date.now();

      if (diff <= 0) {
        setCountdownFinished(true);
        setCountdownParts({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        if (timerId) {
          clearInterval(timerId);
        }
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setCountdownFinished(false);
      setCountdownParts({ days, hours, minutes, seconds });
    };

    updateCountdown();
    timerId = setInterval(updateCountdown, 1000);

    return () => clearInterval(timerId);
  }, [invitation?.event_date]);

  if (loading) {
    return (
      <Box className="invitation-public-loading">
        <CircularProgress color="inherit" />
        <Typography mt={2}>Вчитување...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="invitation-public-loading">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <Box className="invitation-public-container" style={backgroundStyles}>
      <div
        className="invitation-public-stack"
        style={{ color: theme.primary, "--accent-color": theme.secondary }}
      >
        {/* Title */}
        <Box
          component="section"
          className="invitation-section-block invitation-hero"
        >
          <Typography
            variant="h1"
            className="invitation-title"
            style={{ ...(fontStyleMap.title || {}) }}
          >
            {titleParts.length >= 2 ? (
              <>
                <span className="invitation-title-name">{titleParts[0]}</span>
                <span className="invitation-title-ampersand">&</span>
                <span className="invitation-title-name">
                  {titleParts.slice(1).join(" & ")}
                </span>
              </>
            ) : (
              invitation.title
            )}
          </Typography>
        </Box>

        {/* Date & venue */}
        {(primaryDateText || invitation.venue_name) && (
          <Box
            component="section"
            className="invitation-section-block invitation-date-block"
          >
            {primaryDateText && (
              <Typography
                className="invitation-date-primary"
                component="h2"
                style={{
                  color: theme.secondary,
                  ...(fontStyleMap.subtitle || {}),
                }}
              >
                {primaryDateText}
              </Typography>
            )}
            {invitation.venue_name && (
              <Typography
                className="invitation-venue-title"
                component="h2"
                style={{
                  color: theme.primary,
                  ...(fontStyleMap.subtitle || {}),
                }}
              >
                {invitation.venue_name}
              </Typography>
            )}
          </Box>
        )}

        {/* Map */}
        {mapEmbedUrl && (
          <Box
            component="section"
            className="invitation-section-block invitation-map-section"
          >
            <Typography
              className="invitation-section-heading"
              component="h2"
              style={{ ...(fontStyleMap.subtitle || {}) }}
            >
              Локација
            </Typography>
            <Box className="invitation-map-container">
              <iframe
                title="Google Maps"
                src={mapEmbedUrl}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </Box>
          </Box>
        )}

        {(fullDateText || timeText) && (
          <Box
            component="section"
            className="invitation-section-block invitation-date-details"
          >
            <Typography
              className="invitation-date-details-text"
              component="p"
              style={{
                color: theme.primary,
                ...(fontStyleMap.body || {}),
              }}
            >
              {fullDateText && (
                <span className="invitation-date-details-date">{fullDateText}</span>
              )}
              {timeText && (
                <span className="invitation-date-details-time">
                  {timeText}
                </span>
              )}
            </Typography>
          </Box>
        )}

        {agendaItems.length > 0 && (
          <Box
            component="section"
            className="invitation-section-block invitation-agenda-section"
            ref={agendaRef}
            style={{
              "--agenda-accent": theme.secondary,
              "--agenda-progress": `${agendaProgress}%`,
            }}
          >
            <Typography
              className="invitation-section-heading"
              component="h2"
              style={{ ...(fontStyleMap.subtitle || {}) }}
            >
              Распоред
            </Typography>
            <Typography
              className="invitation-agenda-subtitle"
              style={{ ...(fontStyleMap.body || {}) }}
            >
              Драги гости, следете го редоследот и деталите за секој дел од
              прославата.
            </Typography>

            <div className="invitation-agenda-timeline">
              {agendaItems.map((item, index) => {
                const hasAddress = Boolean(item?.address);
                return (
                  <div
                    className={`invitation-agenda-row ${
                      index <= activeAgendaIndex ? "is-active" : ""
                    }`}
                    key={item.step || `agenda-${index}`}
                  >
                    <article className="invitation-agenda-card">
                      {item?.time && (
                        <p className="invitation-agenda-time">
                          <AccessTimeIcon fontSize="small" />
                          <span>{item.time}</span>
                        </p>
                      )}
                      {item?.title && <h3>{item.title}</h3>}
                      {item?.description && (
                        <p className="invitation-agenda-description">{item.description}</p>
                      )}
                      {hasAddress && (
                        <a
                          href={`https://www.google.com/maps?q=${encodeURIComponent(
                            item.address
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="invitation-agenda-location-btn"
                        >
                          Локација
                        </a>
                      )}
                    </article>
                    <div className="invitation-agenda-node-wrap">
                      <span
                        className={`invitation-agenda-node ${
                          index <= activeAgendaIndex ? "is-active" : ""
                        }`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Box>
        )}

        {/* RSVP */}
        <Box component="section" className="invitation-section-block">
          <Typography
            className="invitation-section-heading"
            component="h2"
            style={{ ...(fontStyleMap.subtitle || {}) }}
          >
            RSVP
          </Typography>
          <InvitationRSVPForm
            slug={slug}
            defaultName={guestName}
            defaultGuestId={guestId}
            disabled={isOwner}
            primaryColor={theme.primary}
            accentColor={theme.secondary}
            hideHeading
            fontStyles={fontStyleMap}
            disabledReason={
              isOwner
                ? "Ова е вашата покана. RSVP е наменет за гостите."
                : undefined
            }
          />
        </Box>

        {/* Cover image */}
        {invitation.cover_image_url && (
          <Box component="section" className="invitation-section-block">
            <Typography
              className="invitation-section-heading"
              component="h2"
              style={{ ...(fontStyleMap.subtitle || {}) }}
            >
              Насловна фотографија
            </Typography>
            <div className="invitation-cover">
              <img
                src={invitation.cover_image_url}
                alt={invitation.title}
                onClick={() => setFullscreenImageUrl(invitation.cover_image_url)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setFullscreenImageUrl(invitation.cover_image_url);
                  }
                }}
              />
            </div>
          </Box>
        )}

        {/* Message */}
        {invitation.message && (
          <Box component="section" className="invitation-section-block">
            <Typography
              className="invitation-section-heading"
              component="h2"
              style={{ ...(fontStyleMap.subtitle || {}) }}
            >
              Порака
            </Typography>
            <Typography
              variant="body1"
              className="invitation-message"
              style={{
                ...(fontStyleMap.body || {}),
              }}
            >
              {invitation.message}
            </Typography>
          </Box>
        )}

        {countdownParts && (
          <Box component="section" className="invitation-section-block invitation-countdown-section">
            <Typography
              className="invitation-section-heading"
              component="h2"
              style={{ ...(fontStyleMap.subtitle || {}) }}
            >
              До венчавката останува
            </Typography>
            <div className="invitation-countdown-grid">
              {[
                { key: "days", label: "Дена" },
                { key: "hours", label: "Часа" },
                { key: "minutes", label: "Минути" },
                { key: "seconds", label: "Секунди" },
              ].map(({ key, label }) => (
                <div className="invitation-countdown-item" key={key}>
                  <span
                    className="invitation-countdown-value"
                    style={{ ...(fontStyleMap.title || {}) }}
                  >
                    {String(countdownParts[key]).padStart(2, "0")}
                  </span>
                  <span
                    className="invitation-countdown-label"
                    style={{ ...(fontStyleMap.body || {}) }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
            {countdownFinished && (
              <Typography
                className="invitation-countdown-finished"
                style={{ ...(fontStyleMap.subtitle || {}) }}
              >
                Денот е тука!
              </Typography>
            )}
          </Box>
        )}
      </div>

      {fullscreenImageUrl && (
        <div
          className="invitation-image-lightbox"
          onClick={() => setFullscreenImageUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Целосна фотографија"
        >
          <img
            src={fullscreenImageUrl}
            alt="Invitation full size"
            className="invitation-image-lightbox-content"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </Box>
  );
};

export default InvitationPublicPage;
